import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FeedbackIcon from '@mui/icons-material/Feedback';
import PageLayout from './PageLayout';
import { useAuth } from '../contexts/AuthContext';
import { getContrastText } from '../utils/colorUtils';
import emailjs from '@emailjs/browser';
import { BASIC_PLAN } from '../config/planLimits';

const createHelpSections = () => {
  const basicSummary = `Até ${BASIC_PLAN.limits.maxHabits} hábitos, ${BASIC_PLAN.limits.maxGoals} metas, ${BASIC_PLAN.limits.maxChallenges} desafio, ${BASIC_PLAN.limits.maxNotes} notas e ${BASIC_PLAN.limits.maxEvents} evento ativos ao mesmo tempo.`;
  const playerBasicSummary = `Até ${BASIC_PLAN.limits.maxVideos} vídeos de foco e ${BASIC_PLAN.limits.maxPlaylists} playlist salvos.`;

  return [
    {
      title: 'Planos Habitop',
      items: [
        {
          question: 'Qual a diferença entre os planos Básico e PRO?',
          answer: `O plano Básico oferece ${basicSummary} Além disso, exibe o gráfico "Realizados x Programados" e os cards principais da aba Estatísticas. O plano PRO libera todos os recursos ilimitados, destrava todos os gráficos avançados, ranking completo, matriz de correlação e Player com vídeos e playlists sem limite.`
        },
        {
          question: 'Como faço upgrade para o plano PRO?',
          answer: 'Clique em “Fazer upgrade” em qualquer banner de limites ou na aba Configurações → Plano. Você será direcionado para o checkout seguro da Hotmart para concluir a assinatura.'
        },
        {
          question: 'Onde vejo o comparativo completo antes de contratar?',
          answer: 'Qualquer botão “Fazer upgrade” abre o comparativo de planos com os benefícios atualizados, para que você avalie cada recurso sem sair do app.'
        }
      ]
    },
    {
      title: 'Calendário',
      items: [
        {
          question: 'Como personalizar o layout do calendário?',
          answer: 'Acesse Configurações → Aparência → Layout do calendário para escolher quais blocos exibir, reorganizar a ordem e destacar os dados mais importantes para você.'
        },
        {
          question: 'O que significam as cores e indicadores?',
          answer: 'Verde indica dia perfeito, azul marca hábitos concluídos, amarelo destaca o dia atual e bordas tracejadas mostram dias programados. A legenda fica logo abaixo da grade.'
        },
        {
          question: 'Os eventos aparecem junto dos hábitos?',
          answer: 'Sim. Eventos cadastrados são exibidos no calendário e ajudam a visualizar compromissos lado a lado com seus hábitos diários.'
        }
      ]
    },
    {
      title: 'Hábitos',
      items: [
        {
          question: 'Qual é o limite de hábitos no plano Básico?',
          answer: `Você pode manter até ${BASIC_PLAN.limits.maxHabits} hábitos ativos. O banner informativo avisa quando está chegando ao limite e sugere o upgrade para liberar hábitos ilimitados.`
        },
        {
          question: 'Como criar e organizar hábitos?',
          answer: 'Use o botão “Adicionar hábito”, defina nome, dias da semana e lembretes. Organize-os por prioridade utilizando os filtros e a ordenação disponíveis.'
        },
        {
          question: 'Posso reativar hábitos arquivados?',
          answer: 'Sim. Hábitos desativados permanecem no histórico e podem ser reativados quando quiser, respeitando o limite do seu plano.'
        }
      ]
    },
    {
      title: 'Metas',
      items: [
        {
          question: 'Quantas metas posso manter?',
          answer: `O plano Básico comporta até ${BASIC_PLAN.limits.maxGoals} metas simultâneas. No PRO você trabalha com metas ilimitadas e acompanha o progresso em tempo real nos gráficos.`
        },
        {
          question: 'Como acompanhar o progresso?',
          answer: 'Cada meta possui barras de progresso e subtarefas. Ao concluir subtarefas, o percentual é atualizado automaticamente.'
        },
        {
          question: 'Consigo duplicar metas?',
          answer: 'Sim. Utilize o menu de opções da meta para duplicar estruturas que funcionaram e ganhar tempo criando novos planos.'
        }
      ]
    },
    {
      title: 'Desafios',
      items: [
        {
          question: 'Posso criar quantos desafios?',
          answer: `No plano Básico você mantém ${BASIC_PLAN.limits.maxChallenges} desafio ativo por vez. O PRO libera desafios ilimitados e rankings especiais.`
        },
        {
          question: 'Existem modelos prontos?',
          answer: 'Sim. A aba de desafios oferece modelos personalizáveis para focar em produtividade, saúde, estudos e muito mais.'
        },
        {
          question: 'Como acompanho a evolução do desafio?',
          answer: 'Cada tarefa concluída aumenta o progresso total. Você visualiza o desempenho diário e recebe alertas quando uma etapa está atrasada.'
        }
      ]
    },
    {
      title: 'Notas',
      items: [
        {
          question: 'Qual o limite de notas?',
          answer: `O plano Básico permite registrar até ${BASIC_PLAN.limits.maxNotes} notas. No PRO as notas são ilimitadas e você pode agrupá-las por etiquetas e favoritos.`
        },
        {
          question: 'As notas são salvas automaticamente?',
          answer: 'Sim. Todo conteúdo é sincronizado em tempo real para evitar perdas de informação.'
        },
        {
          question: 'Consigo destacar notas importantes?',
          answer: 'Use o marcador de destaque ou o recurso de favoritos para acessar rapidamente os apontamentos mais relevantes.'
        }
      ]
    },
    {
      title: 'Eventos',
      items: [
        {
          question: 'Como funcionam os eventos?',
          answer: 'Crie eventos com data, horário, descrição e lembrete. Eles aparecem na lista dedicada e no calendário.'
        },
        {
          question: 'Existe limite de eventos?',
          answer: `No Básico você mantém ${BASIC_PLAN.limits.maxEvents} evento ativo por vez. No PRO você registra quantos eventos desejar.`
        },
        {
          question: 'Os eventos influenciam minhas estatísticas?',
          answer: 'Eventos ajudam a planejar sua rotina, mas não impactam diretamente os gráficos de hábitos. Eles servem como referência visual no calendário.'
        }
      ]
    },
    {
      title: 'Estatísticas',
      items: [
        {
          question: 'Quais métricas vejo no plano Básico?',
          answer: 'Você acompanha o gráfico “Realizados x Programados” e os cards de desempenho geral, além de receber alertas personalizados.'
        },
        {
          question: 'O que muda no PRO?',
          answer: 'O PRO libera gráficos avançados: donut programados x realizados, produtividade por dia da semana, ranking de hábitos, matriz de correlação e dotplot mensal completo.'
        },
        {
          question: 'Onde acessar o comparativo de estatísticas?',
          answer: 'Na própria aba Estatísticas há um banner convidando para conhecer o PRO. Clicar em “Ver plano PRO” mostra o comparativo detalhado.'
        }
      ]
    },
    {
      title: 'Player & Pomodoro',
      items: [
        {
          question: 'Qual a diferença do Player entre os planos?',
          answer: `No Básico você salva ${playerBasicSummary} O PRO remove esses limites e permite organizar coleções de áudio ou vídeo para foco prolongado.`
        },
        {
          question: 'O Pomodoro muda entre os planos?',
          answer: 'Não. As mesmas configurações estão disponíveis para Básico e PRO: timers personalizáveis, notificações sonoras e histórico de ciclos.'
        },
        {
          question: 'O player continua rodando enquanto navego?',
          answer: 'Sim. O mini player permanece na base da tela para que você controle reprodução e volume em qualquer aba.'
        }
      ]
    },
    {
      title: 'Dicas de Livros',
      items: [
        {
          question: 'Quem pode acessar as recomendações?',
          answer: 'Tanto o plano Básico quanto o PRO têm acesso integral às dicas de livros, com curadoria por temas e objetivos.'
        },
        {
          question: 'Posso salvar livros favoritos?',
          answer: 'Sim. Clique no ícone de favorito para salvar e consultar depois a sua lista pessoal.'
        },
        {
          question: 'As recomendações são atualizadas?',
          answer: 'As listas são atualizadas semanalmente com novos títulos, resumos e links para compra.'
        }
      ]
    },
    {
      title: 'Configurações & Personalização',
      items: [
        {
          question: 'Quais ajustes visuais posso fazer?',
          answer: 'Escolha tema claro ou escuro, defina a cor principal, personalize sons e reorganize o layout do calendário conforme a sua preferência.'
        },
        {
          question: 'Consigo voltar ao padrão rapidamente?',
          answer: 'Sim. Utilize o botão “Restaurar cor padrão” e a opção “Layout padrão” para resetar cores e organização do calendário.'
        },
        {
          question: 'Onde ajusto notificações e sons?',
          answer: 'Na seção Sons & Notificações você escolhe quais alertas sonoros deseja receber ao concluir hábitos, metas ou ciclos de Pomodoro.'
        }
      ]
    }
  ];
};

function Help({ customColors }) {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const [openFeedback, setOpenFeedback] = useState(false);
  const [feedbackType, setFeedbackType] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    emailjs.init("YOUR_PUBLIC_KEY");
  }, []);

  const helpSections = useMemo(() => createHelpSections(), []);

  const handleOpenFeedback = () => {
    setOpenFeedback(true);
  };

  const handleCloseFeedback = () => {
    setOpenFeedback(false);
    setFeedbackType('');
    setFeedbackMessage('');
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackType || !feedbackMessage) {
      setSnackbar({
        open: true,
        message: 'Por favor, preencha todos os campos',
        severity: 'error'
      });
      return;
    }

    try {
      const templateParams = {
        feedback_type: feedbackType,
        message: feedbackMessage,
        user_email: currentUser?.email || 'Anônimo',
        user_id: currentUser?.uid || 'Anônimo',
        timestamp: new Date().toLocaleString('pt-BR')
      };

      await emailjs.send(
        'service_9jqal6b',
        'template_slhuwwi',
        templateParams,
        'lnpyqE8vVSiyN6044'
      );

      setSnackbar({
        open: true,
        message: 'Feedback enviado com sucesso! Obrigado pela sua contribuição.',
        severity: 'success'
      });
      handleCloseFeedback();
    } catch (error) {
      console.error('Erro ao enviar feedback:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao enviar feedback. Tente novamente mais tarde.',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <PageLayout
      title="Ajuda"
      hideAddButton
      customColors={customColors}
      actionButtons={
        <Button
          variant="contained"
          startIcon={<FeedbackIcon />}
          onClick={handleOpenFeedback}
          sx={{
            bgcolor: customColors?.primary || 'primary.main',
            color: customColors?.primary ? getContrastText(customColors.primary) : theme.palette.primary.contrastText,
            '&:hover': {
              bgcolor: customColors?.primary || 'primary.main',
              opacity: 0.9
            }
          }}
        >
          Enviar Feedback
        </Button>
      }
    >
      <Grid container spacing={3}>
        {helpSections.map((section) => (
          <Grid item xs={12} md={6} key={section.title}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color={customColors?.primary || 'primary.main'}>
                  {section.title}
                </Typography>
                {section.items.map((item, index) => (
                  <Accordion key={index} sx={{ mt: 1 }}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        '&.Mui-expanded': {
                          minHeight: 48,
                          '& .MuiAccordionSummary-content': {
                            margin: '12px 0'
                          }
                        }
                      }}
                    >
                      <Typography>{item.question}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography color="text.secondary">
                        {item.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={openFeedback} onClose={handleCloseFeedback}>
        <DialogTitle>Enviar Feedback</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Tipo de Feedback</InputLabel>
            <Select
              value={feedbackType}
              label="Tipo de Feedback"
              onChange={(e) => setFeedbackType(e.target.value)}
            >
              <MenuItem value="sugestao">Sugestão</MenuItem>
              <MenuItem value="bug">Reportar Bug</MenuItem>
              <MenuItem value="duvida">Dúvida</MenuItem>
              <MenuItem value="outro">Outro</MenuItem>
            </Select>
          </FormControl>
          <TextField
            autoFocus
            margin="dense"
            label="Sua mensagem"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={feedbackMessage}
            onChange={(e) => setFeedbackMessage(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFeedback}>Cancelar</Button>
          <Button onClick={handleSubmitFeedback} variant="contained">
            Enviar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
}

export default Help; 